import { useState, useMemo, useEffect } from 'react';
import { captureError } from "@/lib/logger";
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
} from '@/components/ui/sheet';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Loader2, Check, FileCheck, ClipboardCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import CertificateStep from './steps/CertificateStep';
import ReviewStep from './steps/ReviewStep';

interface WizardState {
    expiresAt: string;
    certificateFile: File | null;
    existingCertificateUrl: string | null;
}

const INITIAL_STATE: WizardState = {
    expiresAt: '',
    certificateFile: null,
    existingCertificateUrl: null,
};

const STEPS = [
    { key: 'certificate', label: 'Certificate', icon: FileCheck, description: 'Date & Document' },
    { key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Confirm Details' },
];

interface AddRoadworthinessWizardProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    contractId: number;
    vehicleId: number;
    roadworthinessId?: number | null;
    onSuccess?: () => void;
}

export default function AddRoadworthinessWizard({
    open,
    onOpenChange,
    contractId,
    vehicleId,
    roadworthinessId,
    onSuccess,
}: AddRoadworthinessWizardProps) {
    const queryClient = useQueryClient();
    const [step, setStep] = useState(0);
    const [state, setState] = useState<WizardState>(INITIAL_STATE);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [direction, setDirection] = useState(0);

    const isEditing = !!roadworthinessId;

    // Fetch existing data when editing
    const { data: existingData } = useQuery({
        queryKey: ['roadworthiness-wizard', roadworthinessId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicle_roadworthiness')
                .select('id, expires_at, certificate_url')
                .eq('id', roadworthinessId)
                .single();
            if (error) throw error;
            return data;
        },
        enabled: open && !!roadworthinessId,
    });

    // Populate form when editing
    useEffect(() => {
        if (existingData && roadworthinessId) {
            setState({
                expiresAt: existingData.expires_at ? format(new Date(existingData.expires_at), 'yyyy-MM-dd') : '',
                certificateFile: null,
                existingCertificateUrl: existingData.certificate_url,
            });
        } else if (open && !roadworthinessId) {
            setState(INITIAL_STATE);
            setStep(0);
        }
    }, [existingData, open, roadworthinessId]);

    // Track original values for dirty checking
    const originalValues = useMemo(() => {
        if (!existingData) return INITIAL_STATE;
        return {
            expiresAt: existingData.expires_at ? format(new Date(existingData.expires_at), 'yyyy-MM-dd') : '',
            certificateFile: null,
            existingCertificateUrl: existingData.certificate_url,
        };
    }, [existingData]);

    const isDirty = useMemo(() => {
        if (roadworthinessId) return false;
        return (
            state.expiresAt !== '' ||
            state.certificateFile !== null
        );
    }, [state, roadworthinessId]);

    const updateState = (updates: Partial<WizardState>) => {
        setState(prev => ({ ...prev, ...updates }));
    };

    // Validation per step
    const canGoNext = (): boolean => {
        switch (step) {
            case 0: // Certificate
                return !!state.expiresAt;
            case 1: // Review
                return true;
            default:
                return false;
        }
    };

    const handleNext = () => {
        if (step < STEPS.length - 1) {
            setDirection(1);
            setStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (step > 0) {
            setDirection(-1);
            setStep(prev => prev - 1);
        }
    };

    const handleClose = () => {
        if (isDirty) {
            setShowCloseConfirm(true);
        } else {
            resetAndClose();
        }
    };

    const resetAndClose = () => {
        setStep(0);
        setState(INITIAL_STATE);
        setShowCloseConfirm(false);
        onOpenChange(false);
    };

    const mutation = useMutation({
        mutationFn: async () => {
            let certificateUrl = state.existingCertificateUrl;

            // Upload certificate if new file provided
            if (state.certificateFile) {
                const ext = state.certificateFile.name.split('.').pop();
                const path = `certificates/${crypto.randomUUID()}.${ext}`;

                const { error: uploadError } = await supabase.storage
                    .from('vehicle-roadworthiness')
                    .upload(path, state.certificateFile);
                if (uploadError) throw uploadError;

                const { data: urlData } = supabase.storage
                    .from('vehicle-roadworthiness')
                    .getPublicUrl(path);
                certificateUrl = urlData.publicUrl;
            }

            if (isEditing) {
                // Update existing
                const { error } = await supabase
                    .from('vehicle_roadworthiness')
                    .update({
                        expires_at: state.expiresAt,
                        certificate_url: certificateUrl,
                    })
                    .eq('id', roadworthinessId);
                if (error) throw error;
            } else {
                // Create new
                const { error } = await supabase
                    .from('vehicle_roadworthiness')
                    .insert({
                        vehicle_id: vehicleId,
                        contract_id: contractId,
                        expires_at: state.expiresAt,
                        certificate_url: certificateUrl,
                    });
                if (error) throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contract-insights'] });
            queryClient.invalidateQueries({ queryKey: ['roadworthiness-wizard'] });
            toast.success(isEditing ? 'Roadworthiness updated' : 'Roadworthiness added');
            resetAndClose();
            onSuccess?.();
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error('Failed to save roadworthiness');
        },
    });

    const isSubmitting = mutation.isPending;

    const variants = {
        enter: (direction: number) => ({
            x: direction > 0 ? 50 : -50,
            opacity: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
        },
        exit: (direction: number) => ({
            x: direction < 0 ? 50 : -50,
            opacity: 0,
        }),
    };

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <CertificateStep
                        expiresAt={state.expiresAt}
                        setExpiresAt={(v) => updateState({ expiresAt: v })}
                        certificateFile={state.certificateFile}
                        setCertificateFile={(f) => updateState({ certificateFile: f })}
                        existingCertificateUrl={state.existingCertificateUrl}
                    />
                );
            case 1:
                return (
                    <ReviewStep
                        expiresAt={state.expiresAt}
                        certificateFile={state.certificateFile}
                        existingCertificateUrl={state.existingCertificateUrl}
                        isEditing={isEditing}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !p-0 !w-[70vw] !max-w-3xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
                >
                    {/* Left Sidebar - Progress */}
                    <div className="w-1/3 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
                        {/* Decorative background elements */}
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

                        <div className="mb-8 relative z-10">
                            <h2 className="text-2xl font-semibold tracking-tight">
                                {isEditing ? 'Edit Roadworthiness' : 'Add Roadworthiness'}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {isEditing ? 'Update roadworthiness certificate details.' : 'Add a new roadworthiness certificate.'}
                            </p>
                        </div>

                        <div className="space-y-1 relative z-10 flex-1">
                            {STEPS.map((s, i) => {
                                const Icon = s.icon;
                                const isActive = i === step;
                                const isCompleted = i < step;

                                return (
                                    <div
                                        key={s.key}
                                        className={cn(
                                            "group flex items-center gap-3 p-3 rounded-xl transition-all duration-300",
                                            isActive ? "bg-background shadow-sm ring-1 ring-border" : "hover:bg-background/50",
                                            isCompleted ? "opacity-70" : "opacity-50",
                                            isActive && "!opacity-100"
                                        )}
                                    >
                                        <div className={cn(
                                            "h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300",
                                            isActive ? "bg-primary text-primary-foreground" :
                                                isCompleted ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground",
                                        )}>
                                            {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                                        </div>
                                        <div>
                                            <p className={cn(
                                                "font-medium text-sm transition-colors",
                                                isActive ? "text-foreground" : "text-muted-foreground"
                                            )}>
                                                {s.label}
                                            </p>
                                            <p className="text-xs text-muted-foreground/70 hidden lg:block">
                                                {s.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Close button in sidebar */}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="mt-auto w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
                            onClick={handleClose}
                        >
                            <X className="h-4 w-4 mr-2" />
                            Cancel & Close
                        </Button>
                    </div>

                    {/* Right Content */}
                    <div className="flex-1 flex flex-col h-full relative">
                        {/* Mobile Header (visible only on small screens) */}
                        <div className="md:hidden p-4 border-b flex items-center justify-between">
                            <div>
                                <h2 className="font-semibold">Step {step + 1} of {STEPS.length}</h2>
                                <p className="text-xs text-muted-foreground">{STEPS[step].label}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={handleClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Main Form Area */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10">
                            <AnimatePresence mode="wait" initial={false} custom={direction}>
                                <motion.div
                                    key={step}
                                    custom={direction}
                                    variants={variants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{
                                        x: { type: "spring", stiffness: 300, damping: 30 },
                                        opacity: { duration: 0.2 }
                                    }}
                                    className="h-full max-w-2xl mx-auto"
                                >
                                    <div className="mb-6">
                                        <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                                        <p className="text-muted-foreground">{STEPS[step].description}</p>
                                    </div>

                                    {renderStep()}
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Sticky Footer */}
                        <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
                            <div className="max-w-2xl mx-auto flex items-center justify-between">
                                <Button
                                    variant="outline"
                                    onClick={handleBack}
                                    disabled={step === 0 || isSubmitting}
                                    className="w-24 rounded-full"
                                >
                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                    Back
                                </Button>

                                {step < STEPS.length - 1 ? (
                                    <Button
                                        onClick={handleNext}
                                        disabled={!canGoNext()}
                                        className="w-32 rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => mutation.mutate()}
                                        disabled={isSubmitting}
                                        className="w-40 rounded-full shadow-lg hover:shadow-emerald-500/25 transition-all bg-emerald-600 hover:bg-emerald-700"
                                    >
                                        {isSubmitting ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Check className="h-4 w-4 mr-2" />
                                        )}
                                        Complete
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to discard them?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Continue Editing</AlertDialogCancel>
                        <AlertDialogAction onClick={resetAndClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
